import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SemanticSuccess } from '../../../../core-components/theme/color-palette/colors';
import { Button } from '../../../../core-components/theme/components/Buttons';
import { visuallyHiddenSx } from '../../../../core-components/theme/components/Fields/fieldStyles';
import { InlineAlert } from '../../../../core-components/theme/components/InlineAlert';
import { SettingsGroup, SettingsRow } from '../../../../core-components/theme/components/SettingsList';
import { HandoffToggle, PushPermissionAsk } from '../../../close-the-loop';
import type { HandoffSaveStatus, HandoffStall, ManageableReason } from '../../../close-the-loop';
import { REDUCED_MOTION, stepBodySx } from '../../constants/layout';
import { fixOf } from '../../domain/doneSummary';
import type { DoneSummary } from '../../domain/doneSummary';
import { stepNoticeOf } from '../../domain/steps';
import type { OnboardingStep, StepNotice } from '../../domain/steps';
import { useOnboardingDone } from '../../hooks/useOnboardingDone';
import type { FinishState } from '../../hooks/useWizardFinish';
import { SettingsGate } from '../SettingsGate';
import { StepFrame } from '../StepFrame';
import type { WizardNav } from '../StepFrame';

import { alertsText, modeGroupsText, morningEmailText, quietHoursText } from './doneText';
import { finishFailedText } from './finishText';

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to { opacity: 1; transform: none; }
`;

const checkSx = (theme: Theme): SystemStyleObject<Theme> => ({
  color: SemanticSuccess[600],
  flexShrink: 0,
  animation: `${scaleIn} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut} both`,
  [REDUCED_MOTION]: { animation: 'none' },
});

const SET_CHECK = <CheckCircleOutlineIcon aria-hidden sx={checkSx} />;

const titleOf = (t: TFunction, summary: DoneSummary | null): string => {
  if (summary === null) return t('onboarding.title.doneLoading', 'Almost done');
  return summary.incomplete === null ? t('onboarding.title.done', 'You’re set') : t('onboarding.title.setMostly', 'You’re set, mostly');
};

const finishNoticeOf = (t: TFunction, finishing: FinishState): StepNotice | null => {
  if (finishing.kind === 'failed') return stepNoticeOf({ failed: finishFailedText(t) });
  return stepNoticeOf({ paused: finishing.kind === 'saving' && finishing.paused });
};

const incompleteText = (t: TFunction, reason: ManageableReason): string => {
  switch (reason) {
    case 'PHONE_NOT_VERIFIED':
      return t('onboarding.done.appOnlyWarning', 'Alerts only show in the app until you verify your phone.');
    case 'NO_CONTACT':
      return t('onboarding.done.noContactWarning', 'Alerts only show in the app until you verify your phone or email.');
    case 'UNKNOWN_ROLE':
      return t('onboarding.done.unknownRoleWarning', 'Your role isn’t set up for alerts yet. Ask your site admin to set your role.');
  }
};

interface IncompleteNoticeProps {
  readonly reason: ManageableReason;
  readonly onVerifyPhone: () => void;
}

const IncompleteNotice: React.FC<IncompleteNoticeProps> = ({ reason, onVerifyPhone }) => {
  const { t } = useTranslation();
  if (fixOf(reason) === 'askAdmin') return <InlineAlert severity="warning" message={incompleteText(t, reason)} />;
  return (
    <InlineAlert
      severity="warning"
      message={incompleteText(t, reason)}
      action={
        <Button intent="secondary" onClick={onVerifyPhone}>
          {t('onboarding.done.verifyPhone', 'Verify my phone')}
        </Button>
      }
    />
  );
};

const ChangeLabel: React.FC<{ label: string }> = ({ label }) => {
  const { t } = useTranslation();
  return (
    <>
      <Box component="span" sx={visuallyHiddenSx}>
        {t('onboarding.done.change', 'Change')}{' '}
      </Box>
      {label}
    </>
  );
};

interface DoneViewProps {
  readonly summary: DoneSummary;
  readonly onEdit: (step: OnboardingStep) => void;
}

const DoneSummaryGroup: React.FC<DoneViewProps> = ({ summary, onEdit }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  return (
    <SettingsGroup aria-label={t('onboarding.done.summary', 'Your setup')}>
      <SettingsRow
        control="button"
        layout="stacked"
        label={<ChangeLabel label={t('onboarding.done.alerts', 'Alerts')} />}
        value={alertsText(t, summary.alerts)}
        description={modeGroupsText(t, summary.modes, locale)}
        onClick={() => onEdit('alerts')}
      />
      <SettingsRow
        layout="stacked"
        label={t('onboarding.done.morningEmail', 'Morning email')}
        value={morningEmailText(t, summary.morningEmail, summary.digestAtMinute, locale)}
      />
      <SettingsRow
        control="button"
        layout="stacked"
        label={<ChangeLabel label={t('onboarding.done.quietHours', 'Quiet hours')} />}
        value={quietHoursText(t, summary.quietHours, locale)}
        onClick={() => onEdit('quiet')}
      />
    </SettingsGroup>
  );
};

interface LeadsGroupProps {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly status: HandoffSaveStatus;
  readonly stall: HandoffStall | null;
  readonly onChange: (next: boolean) => void;
}

const LeadsGroup: React.FC<LeadsGroupProps> = ({ checked, disabled, status, stall, onChange }) => {
  const { t } = useTranslation();
  return (
    <SettingsGroup
      title={t('onboarding.done.forLeads', 'For Leads')}
      footer={
        status === 'failed' && (
          <InlineAlert
            severity="error"
            role="alert"
            message={t('onboarding.done.handoffFailed', 'Couldn’t save the hand-off alarm. Try the switch again.')}
          />
        )
      }
    >
      <HandoffToggle checked={checked} disabled={disabled} status={status} stall={stall} onChange={onChange} />
    </SettingsGroup>
  );
};

const finishLabelOf = (t: TFunction, nav: WizardNav): string => {
  if (nav.returns) return t('onboarding.finishReturn', 'Done');
  if (nav.goesOn) return t('onboarding.finishContinue', 'Continue');
  return t('onboarding.goToIssues', 'Go to Issues');
};

export const DoneStep: React.FC<{ nav: WizardNav }> = ({ nav }) => {
  const { t } = useTranslation();
  const done = useOnboardingDone();
  const { summary } = done;
  return (
    <StepFrame
      nav={nav}
      title={titleOf(t, summary)}
      titleAdornment={summary !== null && summary.incomplete === null ? SET_CHECK : undefined}
      description={t('onboarding.done.help', 'Your alerts are set up. You can change any of this in Profile → Notifications.')}
      primary={{
        label: finishLabelOf(t, nav),
        onPress: nav.finish,
        busy: nav.finishing.kind === 'saving',
      }}
      notice={finishNoticeOf(t, nav.finishing)}
    >
      <SettingsGate load={done.load}>
        {summary !== null && (
          <Box sx={stepBodySx}>
            {summary.incomplete !== null && <IncompleteNotice reason={summary.incomplete} onVerifyPhone={() => nav.goTo('phone')} />}
            <DoneSummaryGroup summary={summary} onEdit={nav.goTo} />
            {done.showHandoff && (
              <LeadsGroup
                checked={done.handoffAlerts}
                disabled={done.handoffDisabled}
                status={done.handoffStatus}
                stall={done.handoffStall}
                onChange={done.setHandoff}
              />
            )}
            <PushPermissionAsk />
          </Box>
        )}
      </SettingsGate>
    </StepFrame>
  );
};
